/* eslint-disable camelcase */
import { faker } from '@faker-js/faker/locale/en';

import { LoanOfferT, MessageT } from '../types';

/**
 * Synthetic values only — mocks must never contain real customer data.
 * Deliberately static rather than faker-generated so Chromatic snapshots stay stable.
 */
export const mockLoanOffer: LoanOfferT = {
  alternate_offer_type: 'installment',
  amortization_flg: false,
  amount: 2500,
  apr: '299.00',
  bank_account_last4: '2589',
  bank_name: 'Mock Bank',
  base_apr: '299.00',
  first_name: 'Carl',
  funding_date: '2026-09-15',
  income_freq_type_cd: 'weekly',
  is_instant_funding_eligible: false,
  lead_id: '123456',
  loan_offer_id: 'a02d344d-aa58-4ca2-807f-e55354179bb3',
  loan_type: 'oec',
  minimum_draw_amount: 50,
  multiple_offers: true,
  oec_apr_reduced: false,
  rbp_report: { name: 'riskview_v6_optics' },
  repayment_method: 'bank_account_ach',
  should_show_rbp: true,
  signing_date: '2026-09-14',
  state: 'AL',
};

export const getMockedAccessToken = (tenant?: string): string => {
  const secretKey = 'fake-secret-key';

  const header = {
    alg: 'RS256',
    kid: 'cashnetusa_dev',
    typ: 'JWT',
  };

  const payload = {
    aud: 'cashnetusa_dev',
    authorized: true,
    client_id: 'dashboard',
    exp: Math.floor(Date.now() / 1000) + (60 * 60), // Expires in 1 hour
    iss: 'https://login-server.enova.test/',
    tenant_id: tenant ?? 'cashnetusa_dev',
    user_id: '123456',
  };

  return `${window.btoa(JSON.stringify(header))}.${window.btoa(JSON.stringify(payload))}.${window.btoa(secretKey)}`;
};

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  month: 'long',
  timeZone: 'CST',
  timeZoneName: 'short',
  year: 'numeric',
};

export const formattedDate = (date: Date) => (
  new Intl.DateTimeFormat('en-US', dateFormatOptions)
    .format(date)
    .replace('at ', '')
);

export const getSimplifiedAccountById200Response = () => ({
  account_id: faker.number.int(),
  first_name: faker.person.firstName(),
  last_name: faker.person.lastName(),
  login: faker.internet.email(),
  open_loan_number: `LOAN${faker.number.int({ max: 999999, min: 100000 })}`,
  open_loan_type: faker.helpers.arrayElement([
    '',
    'netcredit',
    'netcredit_line_of_credit',
    'netcredit_bank_partnership',
    'netcredit_bank_partnership_tab',
    'netcredit_bank_partnership_tab_loc',
    'netcredit_bank_partnership_ccb',
    'netcredit_bank_partnership_ccb_loc',
  ]),
  show_fee_saver_status: faker.datatype.boolean(),
});

type HtmlBodyT = (args: {
  sent_at: string;
  subject: string;
  to: string;
}) => string;

export const htmlBody: HtmlBodyT = ({
  to,
}) => {
  const name = to.split(/[_.0-9]/g)[0];

  return `<div width="100%" style="margin:0;padding:0!important;background-color:#dddddd">
<center style="width:100%;background-color:#dddddd">
<div style="display:none;font-size:1px;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;font-family:
'Open Sans',Arial,sans-serif">
We are writing to inform you of an upcoming withdrawal.
</div>
<div style="display:none;font-size:1px;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;
font-family:'Open Sans',Arial,sans-serif">
</div>
<div style="max-width:680px;margin:0 auto">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin:0 auto">
<tbody><tr>
<td style="padding:25px 20px;background-color:#ffffff;border-bottom:1px solid #dddddd;text-align:left">
<a href="http://link.t.cashnetusa.com/ls/click?upn=u001.lTijx9AIF-2FLawX44B6vGozEza1qS84JMU61Pj4eG911YEQCweUx-2
FqQ1ZesnbGDr1fwln_ajir-2FCH5QmlSvGAz-2BHSWoZ5OE9QcLLm80ne6K-2FHTk4rZV-2B3VdiAExn-2BUx84wCzrAmYzwiMLwJpek-2F4cdpGII3UfhQFl-
2BaYhISBUaJu32CpjSWt16rZekUmZ6yblztzOLWSYs0j9XdFETB8akXMK7xDdu-2FZbrpK5OGYsqD7swgFLoG4RI1KJvobRpvp2odhIt16Y7GUzOiEqaOBZJGv
Zzj1CF3lZt03nYCoz6OYvWjlxCEMhlAXMwoA-2FcY535FbyhOCeCot9NyNxzHckCsg-2B-2BEUDtdoE-2F5G-2FWGCRxywude-2Brr4jm2Bt4gq0DMeQgc78CEm5
7RqbeT-2FiFW-2B6chWwWZHm-2BzwO0P0CkjY-2FLUHZ0SPlLSHQxgHzZZhJQUG6-2FQQnklgFhUTPgcoGkTNVTTeiu6ftMVG8foJ-2BcVOACw74Y2xZJSl3vAui
LryYg-2F6sJ-2BS0fTp17XfIu6ye-2B6qYhK5QRKamkeux2EhAlCC5yg2I-2BsBECviAUEi3ATxRqQA4CGIvzdUdpwMJiutXIx8lXtZqRMAg-3D-3D" 
target="_blank" data-saferedirecturl="https://www.google.com/url?hl=en&amp;q=http://link.t.cashnetusa.com/ls/
click?upn%3Du001.lTijx9AIF-2FLawX44B6vGozEza1qS84JMU61Pj4eG911YEQCweUx-2FqQ1ZesnbGDr1fwln_ajir-2FCH5QmlSvGAz-2BHSWoZ5OE9Q
cLLm80ne6K-2FHTk4rZV-2B3VdiAExn-2BUx84wCzrAmYzwiMLwJpek-2F4cdpGII3UfhQFl-2BaYhISBUaJu32CpjSWt16rZekUmZ6yblztzOLWSYs0j9XdF
ETB8akXMK7xDdu-2FZbrpK5OGYsqD7swgFLoG4RI1KJvobRpvp2odhIt16Y7GUzOiEqaOBZJGvZzj1CF3lZt03nYCoz6OYvWjlxCEMhlAXMwoA-2FcY535Fbyh
OCeCot9NyNxzHckCsg-2B-2BEUDtdoE-2F5G-2FWGCRxywude-2Brr4jm2Bt4gq0DMeQgc78CEm57RqbeT-2FiFW-2B6chWwWZHm-2BzwO0P0CkjY-2FLUHZ0
SPlLSHQxgHzZZhJQUG6-2FQQnklgFhUTPgcoGkTNVTTeiu6ftMVG8foJ-2BcVOACw74Y2xZJSl3vAuiLryYg-2F6sJ-2BS0fTp17XfIu6ye-2B6qYhK5QRKam
keux2EhAlCC5yg2I-2BsBECviAUEi3ATxRqQA4CGIvzdUdpwMJiutXIx8lXtZqRMAg-3D-3D&amp;source=gmail&amp;ust=1739304201154000&amp;us
g=AOvVaw2_tqFLHJsBZXee9iDeKRfh">
<img src="https://ci3.googleusercontent.com/meips/ADKq_NaDwP7ZhbQrKw6kmyhxWHl7zCmDN6Ht4JcigJHH9NyRzvrfLPYSLXAazzQBjck3Z
1UFvfOVuVkBiE4sxpnbfnDFzZGvq6BnzRuHeV2lWqXHuvtq5Vk_l8JZVxXV9PGErassQa10RptJMDQHtmA-Q4DH_8Xh=s0-d-e1-ft#https://s3.us-east-
2.amazonaws.com/email-assets.enova.com/emails_3_0/logos/cnu-logo--color.png" width="185" alt="CashNetUSA" border="0" style
="height:auto;background:#ffffff">
</a>
</td>
</tr>
<tr>
<td style="padding:20px 20px 0;font-family:'Open Sans',sans-serif;font-size:30px;line-height:1.4;color:#333333;
background-color:#ffffff">
<h1 style="margin:0;font-size:30px;line-height:1.4;color:#333333;font-weight:700">Notice: CashNetUSA is no longer 
permitted to withdraw loan payments from your account.</h1>
</td>
</tr>
<tr>
<td align="center" valign="top" style="font-size:0;padding:15px 0 10px;background:#ffffff;background-color:#ffffff">
<table role="presentation" border="0" cellspacing="0" cellpadding="0">
<tbody><tr>
<td valign="top" width="50%">
<div style="display:inline-block;width:100%;padding:10px 0">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td dir="ltr" style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:left;
color:#333333;padding:5px 0px 0px;font-weight:700">
<p style="margin:0">Customer ID #1</p>
</td>
</tr>
</tbody>
</table>
</div>
</td>
<td valign="top" width="50%">
<div style="display:inline-block;vertical-align:top;width:100%;padding:10px 0">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td dir="ltr" 
style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:right;color:#333333;padding:5px 0px
 0px;font-weight:700">
<p style="margin:0">Loan ID #1</p>
</td>
</tr>
</tbody>
</table>
</div>
</td>
</tr>
</tbody></table>
</td>
</tr>
<tr>
<td align="left" style="font-size:0px;padding:10px 20px;background:#ffffff;background-color:#ffffff">
<div style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:left;color:#333333">
<p style="margin:0">Hi ${name},</p>
</div>
</td>
</tr>
<tr>
<td align="left" style="font-size:0px;padding:10px 20px 10px;background:#ffffff;background-color:#ffffff">
<div style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:left;color:#333333">
<p style="margin:0">Our last two attempts to withdraw payment on your loan #1 with CNU of Wisconsin, LLC from your bank 
account ending in 1234 were returned because your account did not contain enough funds to cover the payment. To protect 
your account, federal law prohibits us from trying to withdraw payment again without your permission.
</p>
<p>We may contact you to talk about your payment choices going forward.</p>
</div>
</td>
</tr>
<tr>
<td align="left" valign="top" style="font-size:0;padding:10px 0;background:#ffffff;background-color:#ffffff">
<table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
<tbody><tr>
<td 
style="padding:20px 20px 0;font-family:'Open Sans',sans-serif;font-size:20px;line-height:1.4;color:#333333;text-align:left">
<h2 style="margin:0;font-size:20px;line-height:1.4;color:#333333;font-weight:700;text-align:left">
Previous Payment Attempts</h2>
</td>
</tr>
</tbody></table>
</td>
</tr>
<tr>
<td align="left" valign="top" style="font-size:0;padding:5px 0;background:#ffffff;background-color:#ffffff">
<table role="presentation" border="0" cellspacing="0" cellpadding="0" width="100%">
<tbody><tr>
<td valign="top" style="padding:0 20px;width:100%">
<div style="display:inline-block;width:100%;border-bottom:2px solid #333">
</div>
</td>
</tr>
</tbody></table>
<table role="presentation" width="100%">
<tbody><tr>
<th>Payment due date</th>
<th>Date of attempt</th>
<th>Payment Method</th>
<th>Amount</th>
<th>Fees charged by CashNetUSA</th>
</tr>
<tr>
<td>March 12, 2025</td>
<td>March 11, 2025</td>
<td>Bank Account</td>
<td>$12.00</td>
<td>$5.12</td>
</tr>
<tr>
<td>March 30, 2025</td>
<td>March 25, 2025</td>
<td>Debit Card</td>
<td>$33.00</td>
<td>$15.00</td>
</tr>
</tbody></table>
</td>
</tr>
<tr>
<td align="left" style="font-size:0px;padding:30px 20px;background:#ffffff;background-color:#ffffff">
<div style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:left;color:#333333">
<p style="margin:0">When you make this payment, your principal balance will stay the same and you will not be closer to 
paying off your loan. </p>
</div>
</td>
</tr>
<tr>
<td align="left" style="font-size:0px;padding:0px 20px 25px;background:#ffffff;background-color:#ffffff">
</td>
</tr>
<tr>
<td align="left" style="font-size:0px;padding:0px 20px 25px;background:#ffffff;background-color:#ffffff">
<div style="font-family:'Open Sans',Arial,sans-serif;font-size:16px;line-height:1.4;text-align:left;color:#333333">
<p style="margin:0">If you have any questions or need to stop this withdrawal, contact CashNetUSA at (888) 801-9075 at 
least two days before the payment date. If you wish to make changes to the amount, date, or method of payment, you must 
contact us at least three business days in advance of the payment date noted above. The institution where you have your 
bank account may also be able to assist you.</p>
</div>
</td>
</tr>
</tbody></table>
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width:680px">
<tbody><tr>
<td height="100%" valign="top" width="100%" style="padding:10px;background-color:#ececec">
<table align="left" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width:680px">
<tbody><tr>
<td align="left" valign="top" style="font-size:0">
<div style="display:inline-block;max-width:70%;min-width:210px;vertical-align:top;width:100%">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody><tr>
<td style="padding:10px 10px">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="font-size:13px;text-align:left">
<tbody><tr>
<td style="font-family:'Open Sans',Arial;font-size:13px;line-height:1.4;color:#333333;padding-top:10px">
<a style="font-size:13px;color:#333333;text-decoration:none;font-weight:bold;line-height:1.4">
CashNetUSA<br>
1‌75 W. Jackson Blvd.,<br>
Suite 600, Chicago, IL 60604
</a>
</td>
</tr>
</tbody></table>
</td>
</tr>
</tbody></table>
</div>
<div style="display:inline-block;max-width:30%;min-width:185px;vertical-align:top;width:100%">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody><tr>
<td style="padding:10px 10px">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="font-size:13px;text-align:left">
<tbody><tr>
<td style="font-family:'Open Sans',Arial;font-size:13px;line-height:1.4;color:#2a7ab0;padding-top:10px">
<a href="mailto:support@cashnetusa.com" 
style="color:#2a7ab0;font-weight:700;text-decoration:underline;font-size:13px" 
target="_blank">support@cashnetusa.com</a>
</td>
</tr>
<tr>
<td style="font-family:'Open Sans',Arial;font-size:13px;line-height:1.4;color:#2a7ab0;padding-top:10px">
<a href="tel:8888019075" 
style="color:#2a7ab0;font-weight:700;text-decoration:underline;font-size:13px" target="_blank">888.801.9075</a>
</td>
</tr>
</tbody></table>
</td>
</tr>
</tbody></table>
</div>
</td>
</tr>
</tbody></table>
</td>
</tr>
</tbody></table>
</div>
</center>
</div>`;
};

export const mockMessage: (id?: number) => MessageT = (id) => {
  const sent_at = formattedDate(faker.date.recent());
  const subject = faker.lorem.sentence();
  const to = faker.internet.email();

  return {
    from: faker.internet.email(),
    html_part: htmlBody({
      sent_at,
      subject,
      to,
    }),
    id: id || faker.number.int(),
    sent_at,
    status: faker.helpers.arrayElement([
      'queued',
      'failed',
      'sent',
      'received',
      'income',
      'bccd',
      'bounced',
    ]),
    subject,
    to,
  };
};
